import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import copyTextToClipboard from "../../helper/common/copyToClipboard";
import {
  ABO_COLORS,
  tableBodyCellSx,
  tableHeadCellSx,
} from "../CampaignAbo/lib/campaignAboStyles";

const BODY_FONT_SIZE = "0.75rem";

const zIndexes = {
  header: 5,
  stickyColumn: 6,
};

const bodyTextSx = {
  fontSize: BODY_FONT_SIZE,
  fontWeight: 500,
  color: ABO_COLORS.body,
  lineHeight: 1.45,
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

const STATUS_COLORS = {
  SUCCESS: { bg: "#ECFDF5", color: "#047857", border: "#A7F3D0" },
  PARTIAL_SUCCESS: { bg: "#FFF7ED", color: "#C2410C", border: "#FDBA74" },
  IN_PROGRESS: { bg: "#EFF6FF", color: "#1D4ED8", border: "#93C5FD" },
  PICKED: { bg: "#F5F3FF", color: "#6D28D9", border: "#C4B5FD" },
  FAILURE: { bg: "#FEF2F2", color: "#B91C1C", border: "#FECACA" },
};

function buildColumnsWithStickyOffsets(columns = []) {
  let stickyOffset = 0;
  let lastStickyIndex = -1;

  columns.forEach((column, index) => {
    if (column.isSticky) lastStickyIndex = index;
  });

  return columns.map((column, index) => {
    if (!column.isSticky) {
      return { ...column, stickyLeft: 0, isLastSticky: false };
    }

    const withOffset = {
      ...column,
      stickyLeft: stickyOffset,
      isLastSticky: index === lastStickyIndex,
    };
    stickyOffset += column.width;
    return withOffset;
  });
}

const stickyHeaderCellSx = (column) => ({
  position: "sticky",
  left: column.stickyLeft,
  zIndex: zIndexes.stickyColumn,
  width: column.width,
  minWidth: column.width,
  maxWidth: column.width,
  boxSizing: "border-box",
  bgcolor: ABO_COLORS.tableHeadBg,
  color: ABO_COLORS.tableHeadText,
  fontWeight: 700,
  fontSize: "0.8125rem",
  whiteSpace: "nowrap",
  borderRight: column.isLastSticky
    ? `2px solid ${ABO_COLORS.border}`
    : `1px solid ${ABO_COLORS.border}`,
  py: 1.25,
  px: 1.5,
  overflow: "hidden",
});

const stickyBodyCellSx = (column) => ({
  position: "sticky",
  left: column.stickyLeft,
  zIndex: 2,
  width: column.width,
  minWidth: column.width,
  maxWidth: column.width,
  boxSizing: "border-box",
  bgcolor: ABO_COLORS.cardBg,
  borderRight: column.isLastSticky
    ? `2px solid ${ABO_COLORS.border}`
    : `1px solid ${ABO_COLORS.border}`,
  ...tableBodyCellSx,
  ...bodyTextSx,
  py: 1,
  px: 1.5,
});

const headerCellSx = (width) => ({
  ...tableHeadCellSx,
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: "border-box",
  bgcolor: ABO_COLORS.tableHeadBg,
  borderRight: `1px solid ${ABO_COLORS.border}`,
  px: 1.5,
  overflow: "hidden",
});

const bodyCellSx = (width) => ({
  ...tableBodyCellSx,
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: "border-box",
  bgcolor: ABO_COLORS.cardBg,
  borderRight: `1px solid ${ABO_COLORS.border}`,
  verticalAlign: "top",
  ...bodyTextSx,
  py: 1,
  px: 1.5,
});

const WrappedText = ({ text }) => (
  <Typography component="span" sx={{ display: "block", ...bodyTextSx }}>
    {text ?? "-"}
  </Typography>
);

const RequestDetailsCell = ({ row }) => {
  const lines = [
    {
      label: "Tracking ID",
      value: row.tracking_id_display ?? row.tracking_id ?? "-",
      tooltip: row.tracking_id,
    },
    {
      label: "Task Type",
      value: row.task_type?.replace(/_/g, " ") ?? "-",
    },
  ];

  if (row.task_type === "REPORT_EXPORT") {
    lines.push({
      label: "Display Name",
      value: row?.result?.display_name || "-",
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
      {lines.map((line) => {
        const content = (
          <Typography
            component="span"
            sx={{ ...bodyTextSx, fontSize: "0.7rem" }}
          >
            <Box component="span" sx={{ color: ABO_COLORS.muted, fontWeight: 600 }}>
              {line.label}:
            </Box>{" "}
            {line.value}
          </Typography>
        );

        if (line.tooltip) {
          return (
            <Tooltip key={line.label} title={line.tooltip} arrow placement="top">
              <Box component="span">{content}</Box>
            </Tooltip>
          );
        }

        return (
          <Box key={line.label} component="span">
            {content}
          </Box>
        );
      })}
    </Box>
  );
};

const ActivityTimeCell = ({ row }) => {
  const lines = [
    { label: "Created", value: row.time_created_display },
    { label: "Updated", value: row.time_last_updated_display },
    { label: "Completed", value: row.time_completed_display },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
      {lines.map((line) => (
        <Typography
          key={line.label}
          component="span"
          sx={{ ...bodyTextSx, fontSize: "0.7rem" }}
        >
          <Box component="span" sx={{ color: ABO_COLORS.muted, fontWeight: 600 }}>
            {line.label}:
          </Box>{" "}
          {line.value}
        </Typography>
      ))}
    </Box>
  );
};

const getProgressStyles = (percent, status) => {
  if (status === "FAILURE") {
    return {
      fill: "linear-gradient(90deg, #F87171 0%, #EF4444 100%)",
      track: "#FEE2E2",
      label: "#B91C1C",
    };
  }
  if (status === "PARTIAL_SUCCESS") {
    return {
      fill: "linear-gradient(90deg, #FBBF24 0%, #F59E0B 100%)",
      track: "#FEF3C7",
      label: "#C2410C",
    };
  }
  if (percent >= 100 || status === "SUCCESS") {
    return {
      fill: "linear-gradient(90deg, #34D399 0%, #10B981 100%)",
      track: "#D1FAE5",
      label: "#047857",
    };
  }
  return {
    fill: "linear-gradient(90deg, #818CF8 0%, #6148f6 100%)",
    track: "#EDE9FE",
    label: ABO_COLORS.borderFocus,
  };
};

const BULK_ASIN_TASK_TYPES = [
  "BULK_ASIN_STATUS_ENABLE",
  "BULK_ASIN_STATUS_PAUSE",
  "BULK_ASIN_POPULATE",
];

const SummaryCell = ({ summary, status, retryCount, taskType }) => {
  if (!summary) return <WrappedText text="-" />;

  const isBulkAsinTask = BULK_ASIN_TASK_TYPES.includes(taskType);

  let message =
    summary.message ||
    `Succeeded: ${summary.succeeded_count ?? 0}, Skipped: ${summary.skipped_count ?? 0}, Failed: ${summary.failed_count ?? 0}`;

  if (isBulkAsinTask && !summary.message) {
    message = `${message}, Retry: ${retryCount ?? 0}`;
  }

  if (summary.item_unit === "REPORT_ROW") {
    const processed = summary.processed_count ?? 0;
    const requested = summary.requested_count ?? 0;
    message = `Exported ${processed} of ${requested} rows`;
  }

  const percent = Math.min(
    100,
    Math.max(0, Number(summary.percent_complete) || 0)
  );
  const showProgress = summary.percent_complete != null;
  const progressStyles = getProgressStyles(percent, status);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
      <WrappedText text={message} />

      {showProgress && (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.4,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "0.65rem",
                fontWeight: 600,
                color: ABO_COLORS.muted,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Progress
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 700,
                color: progressStyles.label,
              }}
            >
              {percent}%
            </Typography>
          </Box>

          <Box
            sx={{
              position: "relative",
              height: 8,
              borderRadius: 999,
              bgcolor: progressStyles.track,
              overflow: "hidden",
              boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${percent}%`,
                borderRadius: 999,
                background: progressStyles.fill,
                transition: "width 0.45s ease",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
              }}
            />
          </Box>

          {summary.current_step && summary.current_step !== "COMPLETED" && (
            <Typography
              component="span"
              sx={{
                display: "block",
                mt: 0.4,
                fontSize: "0.65rem",
                fontWeight: 500,
                color: ABO_COLORS.muted,
              }}
            >
              {summary.current_step.replace(/_/g, " ")}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const AsinsCell = ({ asins = [], asinsFileUrl, requestedCount, taskType }) => {
  const handleCopy = async () => {
    if (!asins.length) return;
    try {
      await copyTextToClipboard(asins.join(","));
      toast.success("ASINs copied to clipboard");
    } catch (error) {
      console.log(error);
    }
  };

  const handleDownloadCsv = () => {
    if (!asinsFileUrl) return;
    const link = document.createElement("a");
    link.href = asinsFileUrl;
    link.download = "asins.csv";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasAsins = asins.length > 0;
  const hasFileUrl = Boolean(asinsFileUrl);
  const showTotal =
    BULK_ASIN_TASK_TYPES.includes(taskType) && requestedCount != null;

  if (!hasAsins && !hasFileUrl && !showTotal) return <WrappedText text="-" />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      {showTotal && (
        <Typography
          component="span"
          sx={{ ...bodyTextSx, fontSize: "0.7rem", fontWeight: 600 }}
        >
          <Box component="span" sx={{ color: ABO_COLORS.muted, fontWeight: 600 }}>
            Total:
          </Box>{" "}
          {requestedCount}
        </Typography>
      )}

      {hasFileUrl && !hasAsins && (
        <Button
          variant="text"
          size="small"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={handleDownloadCsv}
          sx={{
            fontSize: "0.6875rem",
            textTransform: "none",
            fontWeight: 600,
            py: 0,
            px: 0,
            minWidth: 0,
            justifyContent: "flex-start",
            color: ABO_COLORS.borderFocus,
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
          }}
        >
          Download CSV
        </Button>
      )}

      {hasAsins && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
          <Box
            sx={{
              flex: 1,
              maxHeight: 72,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#CBD5E1",
                borderRadius: 4,
              },
            }}
          >
            {asins.map((asin) => (
              <Typography
                key={asin}
                component="div"
                sx={{ ...bodyTextSx, fontSize: "0.7rem", lineHeight: 1.5 }}
              >
                {asin}
              </Typography>
            ))}
          </Box>
          <Tooltip title="Copy all ASINs" arrow placement="top">
            <IconButton
              size="small"
              aria-label="Copy all ASINs"
              onClick={handleCopy}
              sx={{
                p: 0.25,
                mt: 0.15,
                flexShrink: 0,
                color: ABO_COLORS.muted,
                "&:hover": { color: ABO_COLORS.borderFocus },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

const StatusCell = ({ status, errorMessage }) => {
  const palette = STATUS_COLORS[status] || {
    bg: "#F8FAFC",
    color: ABO_COLORS.body,
    border: ABO_COLORS.border,
  };

  const chip = (
    <Chip
      label={status?.replace(/_/g, " ") ?? "-"}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.6875rem",
        fontWeight: 600,
        bgcolor: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        textTransform: "capitalize",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );

  if (errorMessage) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {chip}
        <Tooltip title={String(errorMessage)} arrow placement="top">
          <IconButton
            size="small"
            aria-label="View error details"
            sx={{
              p: 0,
              color: "error.main",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return chip;
};

const ReportExportAction = ({ row }) => {
  const downloadUrl = row?.result?.download_url;
  const fileName = row?.result?.file_name;
  const isInProgress = ["PICKED", "IN_PROGRESS"].includes(row.status);

  const handleDownload = () => {
    if (!downloadUrl) return;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName || "report.zip";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (row.status === "SUCCESS" && downloadUrl) {
    return (
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={handleDownload}
        sx={{
          fontSize: "0.6875rem",
          textTransform: "none",
          fontWeight: 600,
          py: 0.25,
          px: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          borderColor: ABO_COLORS.border,
          color: ABO_COLORS.borderFocus,
          "&:hover": {
            borderColor: ABO_COLORS.borderFocus,
            bgcolor: ABO_COLORS.infoBg,
          },
        }}
      >
        Download Report
      </Button>
    );
  }

  if (isInProgress) {
    return <WrappedText text="Processing..." />;
  }

  return <WrappedText text="-" />;
};

const SKELETON_ROW_COUNT = 10;

const RequestTrackingTable = ({
  columns = [],
  rows = [],
  isLoading = false,
  onViewHistoryLogs,
}) => {
  const resolvedColumns = useMemo(
    () => buildColumnsWithStickyOffsets(columns),
    [columns]
  );

  const tableMinWidth = useMemo(
    () => resolvedColumns.reduce((sum, column) => sum + column.width, 0),
    [resolvedColumns]
  );

  const headerLabelSx = {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: ABO_COLORS.tableHeadText,
  };

  const renderCellValue = (row, column) => {
    switch (column.id) {
      case "request_details":
        return <RequestDetailsCell row={row} />;
      case "triggered_by":
        return <WrappedText text={row.triggered_by ?? "-"} />;
      case "activity_time":
        return <ActivityTimeCell row={row} />;
      case "summary":
        return (
          <SummaryCell
            summary={row.summary}
            status={row.status}
            retryCount={row.retry_count}
            taskType={row.task_type}
          />
        );
      case "asins":
        return (
          <AsinsCell
            asins={row.asins}
            asinsFileUrl={row.asins_file_url}
            requestedCount={row.summary?.requested_count}
            taskType={row.task_type}
          />
        );
      case "status":
        return (
          <StatusCell status={row.status} errorMessage={row.error_message} />
        );
      case "action":
        if (row.task_type === "REPORT_EXPORT") {
          return <ReportExportAction row={row} />;
        }
        return (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onViewHistoryLogs?.(row.tracking_id)}
            sx={{
              fontSize: "0.6875rem",
              textTransform: "none",
              fontWeight: 600,
              py: 0.25,
              px: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              borderColor: ABO_COLORS.border,
              color: ABO_COLORS.borderFocus,
              "&:hover": {
                borderColor: ABO_COLORS.borderFocus,
                bgcolor: ABO_COLORS.infoBg,
              },
            }}
          >
            View History Logs
          </Button>
        );
      default:
        return <WrappedText text={row[column.id]} />;
    }
  };

  if (!isLoading && rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 250,
          color: "text.secondary",
          gap: 1.5,
          border: `1px solid ${ABO_COLORS.border}`,
          borderRadius: "8px",
          marginTop: 2,
          bgcolor: ABO_COLORS.cardBg,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
        <Typography variant="subtitle1">No Data Found</Typography>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        overflow: "hidden",
        border: `1px solid ${ABO_COLORS.border}`,
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
        marginTop: 2,
        bgcolor: ABO_COLORS.cardBg,
      }}
    >
      <TableContainer
        sx={{
          maxHeight: 600,
          minHeight: 400,
          overflow: "auto",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            width: tableMinWidth,
            minWidth: tableMinWidth,
            borderCollapse: "separate",
            borderSpacing: 0,
            "& .MuiTableCell-root": {
              boxSizing: "border-box",
            },
          }}
        >
          <colgroup>
            {resolvedColumns.map((column) => (
              <col key={column.id} style={{ width: column.width }} />
            ))}
          </colgroup>

          <TableHead>
            <TableRow
              sx={{
                zIndex: zIndexes.header,
                position: "sticky",
                top: 0,
              }}
            >
              {resolvedColumns.map((column) => {
                const isSticky = column.isSticky;
                const headerSx = {
                  borderBottom: `1px solid ${ABO_COLORS.border}`,
                };

                if (isSticky) {
                  return (
                    <TableCell
                      key={`${column.id}-header-sticky`}
                      align="left"
                      sx={{
                        ...stickyHeaderCellSx(column),
                        ...headerSx,
                      }}
                    >
                      <Typography component="span" noWrap sx={headerLabelSx}>
                        {column.label}
                      </Typography>
                    </TableCell>
                  );
                }

                return (
                  <TableCell
                    key={`${column.id}-header`}
                    align="left"
                    sx={{
                      ...headerCellSx(column.width),
                      ...headerSx,
                      zIndex: zIndexes.header,
                    }}
                  >
                    <Typography component="span" noWrap sx={headerLabelSx}>
                      {column.label}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {resolvedColumns.map((column) => {
                      const isSticky = column.isSticky;
                      return (
                        <TableCell
                          key={`skeleton-${rowIndex}-${column.id}`}
                          align="left"
                          sx={
                            isSticky
                              ? stickyBodyCellSx(column)
                              : bodyCellSx(column.width)
                          }
                        >
                          <Skeleton variant="text" width="80%" height={18} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : rows.map((row, rowIndex) => (
                  <TableRow
                    key={row.tracking_id || rowIndex}
                    hover
                    sx={{
                      "&:hover td": {
                        bgcolor: "#F8FAFC",
                      },
                    }}
                  >
                    {resolvedColumns.map((column) => {
                      const isSticky = column.isSticky;

                      return (
                        <TableCell
                          key={`${column.id}-${rowIndex}`}
                          align="left"
                          sx={
                            isSticky
                              ? stickyBodyCellSx(column)
                              : bodyCellSx(column.width)
                          }
                        >
                          {renderCellValue(row, column)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default RequestTrackingTable;
